<template>
  <div id="poster-creation" class="pui-wrapper">
    <!-- Global spinner -->
    <pui-common-spinner
      v-if="globalLoader.messages.length"
      :message="globalLoader.messages[0].message"
      fullscreen
      hideBackgroundCircle
    />

    <poster-cart
      ref="cart"
      v-if="posterCart.length"
      :getPossibleValues="getPossibleValues"
      @changeForm="currentForm = $event"
      @changeTemplate="currentTemplate = $event"
      @changeSignageItem="currentSignageItem = $event"
      @create="createPostersHook"
    ></poster-cart>

    <poster-main-type-selector :singleLoader="true" v-else>
      <template #title>
        {{ $t('poster.creation.type.title') }}
      </template>

      <template #singleLoader>
        <pui-common-spinner fullscreen hideBackgroundCircle />
      </template>
    </poster-main-type-selector>

    <!-- Confirmation dialog -->
    <pui-send-poster-summary-dialog
      ref="sendPosterSummaryDialog"
      @closed="onSendPosterDialogClosed"
      @creationConfirmed="onSendPosterDialogConfirmation"
      :currentAction="sendAction"
      :generatePreviews="generateSendPreviews"
    >
      <template #posterPreview="{ preview, showAllPrintables }">
        <pui-svg-signage-preview
          :ref="preview.refName"
          :showBackground="false"
          :template="preview.template"
          :bindings="preview.values"
          :types="preview.valuesTypes"
          :showAllPrintables="isRenderingPosterSVGs || showAllPrintables"
        />
      </template>

      <template #previewInformation="{ preview }">
        <p v-for="inputSummary in preview.inputsSummary" :key="inputSummary.alias">
          <span>{{
            $t('poster.creation.send_summary_dialog.item_info.input_label', {
              label: inputSummary.label,
            })
          }}</span>
          {{ inputSummary.value }}
        </p>
        <p>
          <span>{{ $t('poster.creation.send_summary_dialog.item_info.type') }}</span>
          {{ preview.type | piivoTranslate }}
        </p>
        <p>{{ copiesSummary(preview) }}</p>
      </template>
    </pui-send-poster-summary-dialog>

    <!-- Creation previews (for thumbnails) -->
    <template v-if="renderInternalPreviews">
      <pui-svg-signage-preview
        v-for="preview in internalPreviews"
        v-show="false"
        :ref="preview.refName"
        :key="preview.refName"
        :showBackground="false"
        :template="preview.template"
        :bindings="preview.values"
        :types="preview.valuesTypes"
        :showAllPrintables="true"
      />
    </template>
  </div>
</template>

<script>
import { getFormattingManager } from 'piivo-poster-engine/src/lib/services/formattingManager';
import { TemplatesManager } from 'piivo-poster-engine/src/lib/services/templatesManager';
import Vue from 'vue';
import { mapGetters } from 'vuex';

import services from '../../../core/services';
import { LanguagesManagerService } from '../../../core/services/languagesManagerService';
import store from '../../../core/store';
import { Loader } from '../../../utils/loader';
import posterMainTypeSelector from '../components/creation/MainTypeSelector.vue';
import posterCart from '../components/creation/PosterCart.vue';
import puiPosterCreationNotification from '../components/creation/PosterCreationNotification';
import puiSendPosterSummaryDialog from '../components/SendPosterSummaryDialog';
import {
  AttributeAlias,
  CreationActions,
  POSTER_APP_NAME,
  PosterCreationStates,
  Statutes,
} from '../constants';
import ConstantsMixin from '../mixins/ConstantsMixin';
import { getPosterService } from '../services';
import {
  NAMESPACE as POSTER_NAMESPACE,
  SET_CURRENT_SIGNAGE_ITEM_ID,
} from '../store/modules/poster';
import { renderThumbnail } from '../utils/thumbnail';

/**
 * @param {string} sendAction - the send action
 * @returns {boolean} the default value of showAllPrintables
 */
function getDefaultShowAllPrintables(sendAction) {
  return sendAction !== CreationActions.CREATE;
}

export default {
  components: {
    posterMainTypeSelector,
    puiSendPosterSummaryDialog,
    posterCart,
  },
  mixins: [ConstantsMixin],
  data() {
    return {
      globalLoader: new Loader(),

      // Creation actions
      sendAction: null,
      renderInternalPreviews: false,
      internalPreviews: [],
      isRenderingPosterSVGs: false,
      creationSuccess: false,

      // Variables needed to fetch values values
      // (unfortunately they are tied to the current poster)
      currentForm: null,
      currentTemplate: null,
      currentSignageItem: null,

      CreationActions,

      postersToSend: [],
    };
  },
  computed: {
    /**
     * Gets or sets the poster cart in the store
     */

    posterCart: {
      get() {
        return getPosterService('cartState').get();
      },
      set(nextSignageItems) {
        getPosterService('cartState').set(nextSignageItems);
      },
    },
    ...mapGetters({
      user: 'user',
    }),
  },
  methods: {
    // FILTERS
    piivoTranslate(value) {
      return Vue.filter('piivoTranslate')(value);
    },
    /**
     * Computes the copies summary for the preview
     *
     * @param {object} signageItem - the preview with the formatsCopiesMap
     * @returns {string} the copies summary
     */
    copiesSummary(preview) {
      return getFormattingManager().getFormatter('FormatsCopies')(preview.formatsCopiesMap);
    },
    /**
     * @param {object} attribute - the attribute whose options to retrieve
     * @param {object} parameters - search parameters
     * @param {object} parameters.context - context to send with the search
     * @returns {Promise<object[]>} options for the attribute
     */
    getPossibleValues(attribute, { context }) {
      const allGroups = [
        ...(this.currentForm.inputGroups || []),
        ...(this.currentForm.attributeGroups || []),
      ];

      return getPosterService('values').getPossibleValues(
        attribute,
        context,
        this.currentTemplate != null ? this.currentTemplate.templateId : null,
        bodyParameters =>
          getPosterService('values').addAttributeValuesBodyParameters(
            bodyParameters,
            this.currentSignageItem.values,
            allGroups
          )
      );
    },
    // CREATION
    /**
     * Creates the posters
     * @param {object} parameters - creation parameters
     * @returns {Promise} resolves once the render thumbnails is done
     */
    async createPosters(parameters = {}) {
      const stopLoading = this.globalLoader.addMessage(
        this.$t('poster.creation.creation_in_progress')
      );

      services
        .getService('alerts')
        .alertInfo(this.$t(`poster.creation.creation_tasks.alerts.start.${this.sendAction}`));

      // Wait for spinner, alert to show
      await new Promise(resolve => setTimeout(resolve, 100));

      services.getService('analytics').trackEvent(this.Tracking.QUICK_SEND, null, {
        signagesNumber: this.postersToSend.length,
      });

      const notificationId =
        (await services
          .getService('notifications')
          .createNotification(puiPosterCreationNotification, {
            state: PosterCreationStates.IN_PROGRESS,
            action: this.sendAction,
            count: this.postersToSend.length,
            pdfUrls: [],
          })) || -1;
      const { createPostersPromise, renderThumbnailsPromise } = await this.startPosterCreation(
        parameters
      );

      createPostersPromise.catch(() => {
        services
          .getService('alerts')
          .alertError(this.$t(`poster.creation.creation_tasks.alerts.failed.${this.sendAction}`));
        services.getService('notifications').updateNotification(notificationId, {
          state: PosterCreationStates.DONE_ERROR,
        });
      });

      // eslint-disable-next-line promise/catch-or-return
      // By the time both promises are done, we may have changed page.
      // Store any component data necessary into new variables
      const sendAction = this.sendAction;
      Promise.all([
        createPostersPromise,
        renderThumbnailsPromise.catch(() => {
          // Ignore error
        }),
      ])
        // eslint-disable-next-line promise/prefer-await-to-then,  promise/always-return
        .then(() => {
          services
            .getService('alerts')
            .alertSuccess(this.$t(`poster.creation.creation_tasks.alerts.success.${sendAction}`));

          services.getService('notifications').updateNotification(notificationId, {
            state: PosterCreationStates.DONE_SUCCESS,
          });
        })
        .catch(() => null);

      // Creation + (render+patch) are already handled
      // We can stop loading as soon as we initiated those, as long
      // as the rendering is done
      await renderThumbnailsPromise.finally(() => {
        this.reinitializeScreenContext();
        stopLoading();
      });
    },
    /**
     * Creates the posters and then downloads them. The download is done in the background
     * @param {object} opts - creation or download options
     * @returns {Promise} resolves once the render thumbnails is done
     */
    async createAndDownloadPosters(parameters) {
      const stopLoading = this.globalLoader.addMessage(
        this.$t('poster.creation.print_or_download_in_progress')
      );

      // Wait for spinner to show
      await new Promise(resolve => setTimeout(resolve, 100));

      const { createPostersPromise, renderThumbnailsPromise } = await this.startPosterCreation(
        parameters
      );

      createPostersPromise.then(createdPosters => {
        // Do async
        getPosterService('pdfConverter').convertToPdf(
          createdPosters.map(s => s.itemId),
          parameters,
          this.sendAction === CreationActions.DOWNLOAD
            ? this.Tracking.QUICK_DOWNLOAD
            : this.Tracking.QUICK_PRINT
        );
      });

      // Creation + (render+patch) + download pdf are already handled
      // We can stop loading as soon as we initiated those, as long
      // as the rendering is done
      await renderThumbnailsPromise.finally(() => {
        this.reinitializeScreenContext();
        stopLoading();
      });
    },
    /**
     * Retrieves the action's creation form, then either
     * opens the confirmation dialog or directly sends the posters
     *
     * @param {String} sendAction - the type of "send" to execute: create, download...
     * @param {object[]} postersToSend - the posters to send
     */
    async createPostersHook(sendAction, postersToSend) {
      const stopLoading = this.globalLoader.addMessage(
        this.$t('poster.creation.configuration.loading')
      );
      this.postersToSend = postersToSend;

      let hasActionConfigurationError = false;
      let hasActionFormError = false;
      let currentActionForm = null;

      let actionConf = null;

      try {
        actionConf = await getPosterService('sendPosters').retrieveActionConfiguration(sendAction);
      } catch (err) {
        hasActionConfigurationError = true;
        getPosterService('sendPosters').clearActionConfiguration(sendAction);
      }

      if (actionConf && actionConf.form) {
        try {
          currentActionForm = await getPosterService('sendPosters').retrieveActionForm(sendAction);
        } catch (err) {
          hasActionFormError = true;
          getPosterService('sendPosters').clearActionForm(sendAction);
        }
      }

      stopLoading();

      if (hasActionConfigurationError) {
        services
          .getService('alerts')
          .alertError(this.$t('poster.creation.configuration.error_action_configuration'));
        // Since we don't know if there is a form, we don't know if we should
        // display the confirmation dialog: just alert and stop
        return;
      }

      this.sendAction = sendAction;
      const defaultShowAllPrintables = getDefaultShowAllPrintables(this.sendAction);

      if (currentActionForm) {
        this.$refs.sendPosterSummaryDialog.open({
          form: currentActionForm,
          showFormError: hasActionFormError,
          defaultShowAllPrintables,
          totalPosterCount: this.postersToSend.length,
        });
      } else {
        await this.onSendConfirmation({
          values: [
            {
              attributeAlias: AttributeAlias.SHOW_ALL_PRINTABLES,
              value: defaultShowAllPrintables,
            },
          ],
        });
      }
    },
    // CREATION SUMMARY DIALOG
    /**
     * Called when the send poster dialog is closed by itself
     */
    onSendPosterDialogClosed() {
      this.sendAction = null;
    },
    /**
     * Handles the creation dialog summary send confirmation
     *
     * @param {object} opts - creation options
     * @param {object} opts.parameters - parameters to send to creation
     */
    async onSendPosterDialogConfirmation({ parameters }) {
      await this.onSendConfirmation(parameters);

      if (this.$refs.sendPosterSummaryDialog) {
        this.$refs.sendPosterSummaryDialog.closeSilently();
      }
    },
    // PREVIEWS
    /**
     * @returns {string} a unique id for the poster preview
     */
    getCreationPreviewId(poster, formatId) {
      return `${poster.id}_${formatId}`;
    },
    /**
     * @returns {string} a unique id to be used for the poster preview ref
     */
    getCreationPreviewRefName(poster, formatId) {
      return `preview_${this.getCreationPreviewId(poster, formatId)}`;
    },
    /**
     * Generates preview poster objects for all formats of the posters
     * @param {object[]} posters - the posters for which to generate previews
     */
    generatePreviewData(posters) {
      const previews = [];

      for (const poster of posters) {
        const type = getPosterService('templates').getTypeData(poster.type.itemId);

        for (const formatId in poster.formatsCopiesMap) {
          const currentFormatCopies = poster.formatsCopiesMap[formatId];
          if (!getPosterService('signages').checkFormatCopies(poster.formatsCopiesMap, formatId)) {
            continue;
          }

          // Build preview object
          const templateCopy = JSON.parse(
            JSON.stringify(type.templates.get(formatId), (key, value) => {
              return key !== 'computedTemplate' ? value : null;
            })
          );
          const valuesCopy = JSON.parse(JSON.stringify(poster.values));
          TemplatesManager.initProperties(templateCopy, valuesCopy);

          const id = this.getCreationPreviewId(poster, formatId);
          const preview = {
            id,
            refName: this.getCreationPreviewRefName(poster, formatId),
            template: templateCopy, // Clone template to prevent templates modifications
            values: valuesCopy,
            valuesTypes: type.valuesTypes,
            inputsSummary: poster.inputsSummary,
            type: poster.type,
            formatsCopiesMap: { [formatId]: currentFormatCopies },
          };

          previews.push(preview);
        }
      }

      return previews.filter(p => !!p.template && !!p.values);
    },
    /**
     * Generates previews for the send dialog, for the given page range
     * @param {number} pageIndex - index of the page of previews to generate
     * @param {number} pageSize - the number of previews per page
     */
    generateSendPreviews(pageIndex, pageSize) {
      const start = pageIndex * pageSize;
      const end = start + pageSize;
      return this.generatePreviewData(this.postersToSend.slice(start, end));
    },
    // SEND
    /**
     * Handles the send confirmation via `this.sendAction`
     *
     * @param {object} parameters - parameters to send to creation
     */
    async onSendConfirmation(parameters) {
      this.renderInternalPreviews = true;
      if (this.sendAction === CreationActions.DOWNLOAD) {
        await this.createAndDownloadPosters(parameters);
      } else {
        await this.createPosters(parameters);
      }
      this.renderInternalPreviews = false;
      this.sendAction = null;
    },
    /**
     * Starts the creation and render+patch thumbnails process
     * @param {object} parameters - creation parameters
     * @returns {{ createPostersPromise: Promise, renderThumbnailsPromise: Promise }} the promises
     * of creation and thumbnail render. The patch thumbnails is done automatically
     */
    async startPosterCreation(parameters) {
      // Reset success on creation start
      this.creationSuccess = true;
      const status =
        this.sendAction === CreationActions.PRINT_AUTO
          ? Statutes.WAITING_TO_BE_PRINTED
          : Statutes.TO_BE_PRINTED;

      // Always render printables so they will appear in the thumbnails
      // It doesn't matter if they are included in the svgs sent to creation:
      // they can be hidden by the pdf api
      this.isRenderingPosterSVGs = true;
      // Wait for printables to be displayed
      await this.$nextTick();

      const { builtPosters, correlationPosters } = await this.createCorrelationPosters(status);
      // Launch creation and thumbnails at the same time
      const createPostersPromise = getPosterService('signages').createSignages(
        builtPosters,
        parameters
      );
      const renderThumbnailsPromise = this.renderThumbnails(correlationPosters);

      // Do not await here because we want to patch
      // the thumbnails in parallel
      // eslint-disable-next-line promise/prefer-await-to-then, promise/catch-or-return
      createPostersPromise
        // eslint-disable-next-line promise/always-return, promise/prefer-await-to-then
        .then(() => {
          this.creationSuccess = true;
          getPosterService('signagesSync').startSynchronizationDelay();
        })
        .catch(() => {
          this.creationSuccess = false;
        });

      // eslint-disable-next-line promise/prefer-await-to-then, promise/catch-or-return
      renderThumbnailsPromise
        .catch(error => {
          services
            .getService('analytics')
            .trackException(error, this.Tracking.QUICK_THUMBNAIL_ERROR);
        })
        .finally(() => {
          // The last one to access the poster svgs is renderThumbnails.
          // So we can safely set this to false once it's done
          this.isRenderingPosterSVGs = false;
        });

      Promise.all([createPostersPromise, renderThumbnailsPromise])
        // eslint-disable-next-line promise/prefer-await-to-then, promise/always-return
        .then(([createdPosters, correlationPostersWithThumbnails]) => {
          this.patchSignagesWithThumbnails(createdPosters, correlationPostersWithThumbnails);
        })
        .catch(() => null);

      return { createPostersPromise, renderThumbnailsPromise };
    },
    /**
     * Updates the created posters with their correlation thumbnails
     *
     * @param createdPosters - the created posters
     * @param correlationPosters - the correlation posters with the rendered thumbnails
     * @returns nothing, the update is done in the background
     */
    patchSignagesWithThumbnails(createdPosters, correlationPosters) {
      if (
        !createdPosters ||
        !createdPosters.length ||
        !correlationPosters ||
        !correlationPosters.length
      ) {
        return;
      }

      const correlationSignagesMap = new Map(
        correlationPosters.map(correlationPosters => [
          correlationPosters.correlationId,
          correlationPosters.thumbnail,
        ])
      );

      const patchSignages = createdPosters
        .map(createdSignage => ({
          itemId: createdSignage.itemId,
          thumbnail: correlationSignagesMap.get(createdSignage.correlationId),
        }))
        .filter(p => p.thumbnail);

      if (!patchSignages.length) {
        return;
      }

      // Do this async
      getPosterService('signages')
        .patchLogsWithThumbnails(patchSignages)
        .catch(error => {
          services
            .getService('analytics')
            .trackException(error, this.Tracking.QUICK_THUMBNAIL_ERROR);
        });
    },
    /**
     * Builds poster objects for creation, and correlation posters
     * for thumbnail update
     *
     * @param {string} status - the status to set in the built poster objects
     * @returns {{buildPosters: object[]; correlationPosters: []}} the built posters
     * and the correlation posters
     */
    async createCorrelationPosters(status) {
      const builtPosters = [];
      const correlationPosters = [];

      const currentLanguage = LanguagesManagerService.getSelectedLocale();

      for (const poster of this.postersToSend) {
        this.internalPreviews = this.generatePreviewData([poster]);
        // Wait for preview to show
        await new Promise(resolve => setTimeout(resolve, 10));

        for (const formatId in poster.formatsCopiesMap) {
          const areFormatCopiesValid = getPosterService('signages').checkFormatCopies(
            poster.formatsCopiesMap,
            formatId
          );
          if (!areFormatCopiesValid) {
            continue;
          }

          const typeData = getPosterService('templates').getTypeData(poster.type.itemId);

          // Build poster
          const builtPoster = getPosterService('signages').buildSignage(
            poster,
            typeData,
            formatId,
            status,
            currentLanguage
          );
          builtPosters.push(builtPoster);

          // Set builtPoster SVG

          let previewSvgRef = this.$refs[this.getCreationPreviewRefName(poster, formatId)];
          if (Array.isArray(previewSvgRef)) {
            // In slot, no need to get ref[0]. But we still do if we rendered internally
            previewSvgRef = previewSvgRef[0];
          }
          if (previewSvgRef) {
            const svgEl = previewSvgRef.getSvgEl();
            const template = previewSvgRef.getTemplate();
            const svgStr = getPosterService('signages').generateSvgFromPreview(svgEl, template);

            builtPoster.renderContents = [svgStr];
            correlationPosters.push({
              correlationId: builtPoster.correlationId,
              svgEl,
              template,
            });
          }
        }
      }

      this.internalPreviews = [];

      return { builtPosters, correlationPosters };
    },
    /**
     * Renders the thumbnails for the correlation posters
     * @param {{ svgEl: Element; computedTemplate: Object}[]} correlationPosters - array
     * of correlation posters to render thumbnails for
     * @returns promise of all the thumbnails (ignores individual errors)
     */
    async renderThumbnails(correlationPosters) {
      const renderPromises = correlationPosters.map(async correlationPoster => {
        let thumbnail = null;

        try {
          thumbnail = await renderThumbnail(
            correlationPoster.svgEl,
            correlationPoster.template.computedTemplate
          );
        } catch (error) {
          services
            .getService('analytics')
            .trackException(error, this.Tracking.QUICK_THUMBNAIL_ERROR);
        }

        return { correlationId: correlationPoster.correlationId, thumbnail };
      });

      return Promise.all(renderPromises);
    },
    /**
     * Reinitialize screen context: resets signage items, types, template
     */
    reinitializeScreenContext() {
      // Remove the sent posters from the cart
      this.posterCart = this.posterCart.filter(
        cartPoster => !this.postersToSend.find(sentPoster => sentPoster.id === cartPoster.id)
      );
      this.postersToSend = [];

      // Set the current poster to be the first item in the cart if it exists
      const nextCurrentSignageItem = this.posterCart.length ? this.posterCart[0] : null;
      if (nextCurrentSignageItem && this.$refs.cart) {
        this.$refs.cart.setCurrentSignageItem(nextCurrentSignageItem, true);
        // This will trigger the cart event to update our current item, form, template
        return;
      }

      // Redirect to batch creation if it is the default page
      const posterApp = services.getService('applications').getAppDetails(POSTER_APP_NAME);
      const batchCreationRoute = this.$router.resolve({ name: 'posterBatchCreation' });
      if (
        posterApp &&
        posterApp.route &&
        batchCreationRoute &&
        posterApp.route === batchCreationRoute.route.path
      ) {
        this.$router.push({ name: 'posterBatchCreation' });
        return;
      }

      // Otherwise stay on this page.
      // Manually clear our current item, form, template
      this.currentSignageItem = null;
      store.commit(`${POSTER_NAMESPACE}/${SET_CURRENT_SIGNAGE_ITEM_ID}`, null);
      this.currentForm = null;
      this.currentTemplate = null;
    },
  },
};
</script>
